import axios, { AxiosError } from 'axios';
import {
  QueriedData,
  showErrorNotification,
  showSuccessNotification,
} from '../assets/useAssets';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Center, CentersManyResponse } from 'src/api/booking/data-contracts';
import {
  Customer,
  CustomerWriteModel,
  createCustomer,
  deleteCustomer,
  getAllCustomers,
  updateCustomer,
} from 'src/api/customers';

export interface UseCustomersResult {
  customersQuery: QueriedData<Customer[]>;
  createCustomer: (customer: CustomerWriteModel) => void;
  updateCustomer: (data: { id: string; customer: CustomerWriteModel }) => void;
  archiveCustomer: (customerID: string) => void;
}

function useCreateCustomer() {
  const queryClient = useQueryClient();
  const { mutate: create } = useMutation(
    (customer: CustomerWriteModel) => createCustomer(customer),
    {
      onSuccess: () => {
        showSuccessNotification('Cliente Final criado com sucesso');
        queryClient.invalidateQueries(['final-customers']);
        queryClient.invalidateQueries(['final-customers', 'centers']);
      },
      onError: () => {
        showErrorNotification('Ocorreu um erro inesperado');
      },
    },
  );
  return create;
}

export function useCustomers(): UseCustomersResult {
  const query = useQuery({
    queryFn: async () => getAllCustomers(),
    queryKey: ['final-customers'],
  });

  const create = useCreateCustomer();
  const queryClient = useQueryClient();
  const { mutate: archive } = useMutation(
    (customerID: string) => deleteCustomer(customerID),
    {
      onSuccess: () => {
        showSuccessNotification('Cliente Final arquivado com sucesso');
        queryClient.invalidateQueries(['final-customers']);
        queryClient.invalidateQueries(['final-customers', 'centers']);
      },
      onError: (err: AxiosError) => {
        if (!axios.isAxiosError(err) || err.response?.status !== 400) {
          showErrorNotification('Ocorreu um erro inesperado');
        }

        showErrorNotification(
          'Erro ao arquivar cliente final: ' + err.response?.data,
        );
      },
    },
  );
  const updateCustomer = useUpdateCustomer();
  return {
    customersQuery: query,
    createCustomer: create,
    archiveCustomer: archive,
    updateCustomer,
  };
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  const { mutate: update } = useMutation(
    ({ id, customer }: { id: string; customer: CustomerWriteModel }) =>
      updateCustomer(id, customer),
    {
      onSuccess: (_, { id }) => {
        showSuccessNotification('Cliente Final editado com sucesso');
        queryClient.invalidateQueries(['final-customers', id]);
        queryClient.invalidateQueries(['final-customers']);
        queryClient.invalidateQueries(['final-customers', 'centers']);
      },
      onError: (err: AxiosError) => {
        if (!axios.isAxiosError(err) || err.response?.status !== 400) {
          showErrorNotification('Ocorreu um erro inesperado');
        }

        showErrorNotification(
          'Erro ao editar cliente final: ' + err.response?.data,
        );
      },
    },
  );
  return update;
}
export type CustomerAggregated = Customer & {
  center: Center;
};
export function useCustomersAggregated(
  customersQuery: QueriedData<Customer[]>,
  centersQuery: QueriedData<CentersManyResponse>,
) {
  const queryKey = ['final-customers, centers'];

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<CustomerAggregated[]> => {
      if (!customersQuery.data || !centersQuery.data) {
        return [];
      }

      const centerMap: Record<string, Center> = {};
      centersQuery.data.centers.forEach(
        (center) => (centerMap[center.id] = center),
      );
      return customersQuery.data.map((customer) => ({
        ...customer,
        center: centerMap[customer.centerID],
      }));
    },
    enabled: !!customersQuery.data && !!centersQuery.data,
  });

  return query;
}
